import { OppButton } from '../components/ui/control';
import { buttonVariants } from '../components/ui/controlStyles';
import { Link, useSearchParams } from 'react-router-dom';
import { useSharedAuth } from '@litopia/shared';
import {
  PenLine, Heart, Clock, Truck,
  ChevronRight, Feather, Flame,
  BookOpen, ArrowRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { logError } from '../utils/errorHandler';
import { useCopy } from '../copy';
import {
  usePublicLetters,
  useFollowingLetters,
  useToggleLike,
  useMyLikedLetterIds,
  useMuseumItems,
  useMyStats,
  useMyCourier,
} from '../hooks/useOpp';
import { LetterCardSkeleton } from '../components/ui/Skeleton';
import { SemanticState } from '../components/semantic/SemanticState';

type FeedTab = 'recommended' | 'latest' | 'following';

export default function HomePage() {
  const { user } = useSharedAuth();
  const { t } = useCopy();
  const [searchParams] = useSearchParams();
  const requestedFeed = searchParams.get('feed');
  const feedTab: FeedTab = requestedFeed === 'latest' || requestedFeed === 'following'
    ? requestedFeed
    : 'recommended';
  const publicFeedOrder = feedTab === 'latest' ? 'latest' : 'recommended';
  const { data: allLetters, isLoading: loadingAll } = usePublicLetters({
    limit: 20,
    order: publicFeedOrder,
    enabled: feedTab !== 'following',
  });
  const { data: followingLetters, isLoading: loadingFollowing } = useFollowingLetters(20, feedTab === 'following');
  const { data: museumData } = useMuseumItems({ pageSize: 5 });
  const museumItems = museumData?.items ?? [];
  const { data: likedIds } = useMyLikedLetterIds();
  const toggleLikeMutation = useToggleLike();
  const { data: myStats } = useMyStats(user?.id);
  const { data: myCourier } = useMyCourier();

  const letters = feedTab === 'following' ? (followingLetters ?? []) : (allLetters ?? []);
  const isLoading = feedTab === 'following' ? loadingFollowing : loadingAll;

  const toggleLike = (id: string) => {
    if (!user) return;
    toggleLikeMutation.mutate(
      { letterId: id, userId: user.id },
      {
        onError: (err) => {
          logError(err, 'Toggle like');
          toast.error(err instanceof Error ? err.message : 'Failed to update');
        },
      }
    );
  };

  const isLiked = (id: string) => likedIds?.has(id) ?? false;

  const TAGS = [
    { label: t('museum.filter.future'), color: 'bg-[hsl(var(--opp-accent-soft))] text-[hsl(var(--opp-accent-strong))]' },
    { label: t('museum.filter.memory'), color: 'bg-[hsl(var(--opp-surface-raised))] text-[hsl(var(--opp-accent-strong))]' },
    { label: t('museum.filter.warmth'), color: 'bg-[hsl(var(--opp-surface-raised))] text-[hsl(var(--opp-muted))]' },
    { label: t('museum.filter.story'), color: 'bg-[hsl(var(--opp-surface-raised))] text-[hsl(var(--opp-muted))]' },
    { label: t('museum.filter.friendship'), color: 'bg-[hsl(var(--opp-surface-raised))] text-[hsl(var(--opp-muted))]' },
  ];
  const feedLinks = [
    { id: 'recommended', label: t('feed.tab.recommended'), href: '/?feed=recommended' },
    { id: 'latest', label: t('feed.tab.latest'), href: '/?feed=latest' },
    { id: 'following', label: t('feed.tab.following'), href: '/?feed=following' },
  ] as const;

  return (
    <div className="space-y-8 pb-8">
      {/* ===== Banner ===== */}
      <section className="opp-home-hero relative isolate overflow-hidden rounded-hero border border-[hsl(var(--opp-border))] p-6 shadow-opp-hero md:p-10">
        <div className="opp-home-hero-decoration absolute -right-8 -top-8 h-32 w-32 rounded-full" />
        <div className="opp-home-hero-decoration absolute -bottom-4 -left-4 h-24 w-24 rounded-full" />
        <div className="relative flex min-h-[var(--litopia-layout-170px)] items-center justify-between gap-6 md:min-h-[var(--litopia-layout-190px)]">
          <div className="flex-1 min-w-0">
            <h2 className="font-serif text-2xl md:text-3xl font-bold text-[hsl(var(--opp-ink))] leading-tight whitespace-pre-line">
              {t('feed.banner.title')}
            </h2>
            <p className="mt-2 text-sm text-[hsl(var(--opp-accent-strong))]">
              {t('feed.banner.subtitle')}
            </p>
            <Link
              to="/write"
              className={buttonVariants({ variant: 'primary', className: 'mt-5 inline-flex min-h-11 items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold shadow-sm' })}
            >
              <PenLine className="h-4 w-4" />
              {t('feed.banner.btn')}
            </Link>
          </div>
          {/* Decorative letter illustration */}
          <div aria-hidden="true" className="hidden md:block shrink-0">
            <div className="relative w-32 h-28">
              <div className="absolute inset-0 bg-[hsl(var(--opp-surface-raised)/.5)] rounded-lg transform rotate-6" />
              <div className="absolute inset-0 bg-[hsl(var(--opp-surface-raised)/.6)] rounded-lg transform -rotate-3" />
              <div className="absolute inset-2 bg-[hsl(var(--opp-surface-raised)/.7)] rounded-lg flex items-center justify-center">
                <Feather className="h-10 w-10 text-[hsl(var(--opp-accent)/.42)]" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <nav
        aria-label="信件内容筛选"
        role="tablist"
        className="opp-page-context-navigation flex min-h-12 items-end gap-6 overflow-x-auto border-b border-[hsl(var(--opp-border))] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {feedLinks.map((item) => {
          const isActive = feedTab === item.id;
          return (
            <Link
              key={item.id}
              to={item.href}
              id={`home-feed-tab-${item.id}`}
              role="tab"
              aria-selected={isActive}
              aria-controls="home-feed-panel"
              className={`flex min-h-12 min-w-11 shrink-0 touch-manipulation items-center justify-center border-b-2 px-3 text-sm font-medium transition-[border-color,color,transform] active:scale-[var(--litopia-component-button-press-scale)] ${
                isActive
                  ? 'border-[hsl(var(--opp-accent))] text-[hsl(var(--opp-ink))]'
                  : 'border-transparent text-[hsl(var(--opp-muted))] hover:text-[hsl(var(--opp-ink))]'
              }`}
            >
              {item.label}
            </Link>
          );
        })}
      </nav>

      {/* ===== Two Column Layout ===== */}
      <div className="grid grid-cols-1 items-start gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(280px,340px)]">
        {/* ===== Left: Feed ===== */}
        <div id="home-feed-panel" role="tabpanel" aria-labelledby={`home-feed-tab-${feedTab}`} className="space-y-4 min-w-0">
          {/* Letter Feed */}
          {isLoading ? (
            <div className="space-y-4">
              <LetterCardSkeleton />
              <LetterCardSkeleton />
              <LetterCardSkeleton />
            </div>
          ) : letters && letters.length > 0 ? (
            <div className="space-y-4">
              {letters.map((letter) => (
                <LetterFeedCard
                  key={letter.id}
                  letter={letter}
                  isLiked={isLiked(letter.id)}
                  onToggleLike={() => toggleLike(letter.id)}
                  t={t}
                />
              ))}
            </div>
          ) : feedTab === 'following' ? (
            <SemanticState
              kind="empty"
              illustration="empty-following"
              title={t('feed.following.empty')}
              action={<Link to="/discover" className={buttonVariants({ variant: 'primary', className: 'inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold' })}>{t('feed.following.cta')}<ChevronRight className="h-4 w-4" /></Link>}
            />
          ) : (
            <SemanticState
              kind="empty"
              illustration="empty-inbox"
              title={t('feed.empty')}
              action={<Link to="/write" className={buttonVariants({ variant: 'primary', className: 'inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold' })}><PenLine className="h-4 w-4" />{t('feed.writeCard.btn')}</Link>}
            />
          )}
        </div>

        {/* ===== Right: Sidebar ===== */}
        <aside className="hidden lg:block space-y-5 sticky top-20 max-h-[calc(100vh-6rem)] overflow-y-auto">
          {/* Write Card */}
          <div className="letter-card rounded-xl p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[hsl(var(--opp-accent-soft))]">
                <PenLine className="h-5 w-5 text-[hsl(var(--opp-accent-strong))]" />
              </div>
              <div>
                <p className="text-sm font-semibold text-[hsl(var(--opp-ink))]">{t('feed.writeCard.title')}</p>
                <p className="text-xs text-[hsl(var(--opp-muted))]">{t('feed.writeCard.desc')}</p>
              </div>
            </div>
            <Link
              to="/write"
              className={buttonVariants({ variant: 'primary', className: 'flex items-center justify-center gap-2 w-full rounded-lg py-2.5 text-sm font-semibold' })}
            >
              {t('feed.writeCard.btn')}
              <ChevronRight className="h-4 w-4" />
            </Link>
          </div>

          {/* Hot Letters */}
          <div className="letter-card rounded-xl p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="flex items-center gap-1.5 text-sm font-semibold text-[hsl(var(--opp-ink))]">
                <Flame className="h-4 w-4 text-[hsl(var(--opp-accent-strong))]" />
                {t('feed.hotLetters')}
              </h3>
              <Link to="/discover" className="inline-flex min-h-11 items-center px-1 text-xs text-[hsl(var(--opp-muted))] hover:text-[hsl(var(--opp-accent-strong))]">
                {t('common.viewAll')}
              </Link>
            </div>
            <div className="space-y-3">
              {(museumItems ?? []).slice(0, 3).map((item) => (
                <div key={item.id} className="flex items-start gap-2.5">
                  <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[hsl(var(--opp-surface-raised))] text-xs font-bold text-[hsl(var(--opp-accent-strong))]">
                    {item.title?.charAt(0) ?? '?'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[hsl(var(--opp-ink))] truncate">{item.title}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="flex items-center gap-0.5 text-caption text-[hsl(var(--opp-muted))]">
                        <Heart className="h-3 w-3" /> {item.like_count ?? 0}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
              {(!museumItems || museumItems.length === 0) && (
                <SemanticState compact illustration="empty-collection" kind="empty" title={t('museum.empty')} />
              )}
            </div>
          </div>

          {/* Courier Stats */}
          <div className="letter-card rounded-xl p-5">
            <h3 className="flex items-center gap-1.5 text-sm font-semibold text-[hsl(var(--opp-ink))] mb-3">
              <Truck className="h-4 w-4 text-[hsl(var(--opp-accent-strong))]" />
              {t('feed.courierStats')}
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-lg bg-[hsl(var(--opp-surface-raised))] p-3 text-center">
                <p className="text-lg font-bold text-[hsl(var(--opp-ink))]">{myCourier ? (myCourier.level ?? 1) : '--'}</p>
                <p className="text-caption text-[hsl(var(--opp-muted))]">{t('feed.courierStats.active')}</p>
              </div>
              <div className="rounded-lg bg-[hsl(var(--opp-surface-raised))] p-3 text-center">
                <p className="text-lg font-bold text-[hsl(var(--opp-ink))]">{myStats?.letters_sent ?? '--'}</p>
                <p className="text-caption text-[hsl(var(--opp-muted))]">{t('feed.courierStats.delivered')}</p>
              </div>
            </div>
            <Link
              to="/me?tab=courier"
              className="mt-3 flex items-center justify-center gap-1.5 w-full rounded-lg border border-[hsl(var(--opp-border))] py-2 text-xs font-medium text-[hsl(var(--opp-accent-strong))] hover:bg-[hsl(var(--opp-surface-raised))] transition-colors"
            >
              <Truck className="h-3.5 w-3.5" />
              {t('feed.courierStats.join')}
            </Link>
          </div>

          {/* Tags */}
          <div className="letter-card rounded-xl p-5">
            <h3 className="text-sm font-semibold text-[hsl(var(--opp-ink))] mb-3">{t('feed.tags')}</h3>
            <div className="flex flex-wrap gap-2">
              {TAGS.map((tag) => (
                <span
                  key={tag.label}
                  className={`rounded-full px-3 py-1 text-xs font-medium ${tag.color}`}
                >
                  {tag.label}
                </span>
              ))}
            </div>
          </div>

          {/* Museum quick link */}
          <Link
            to="/discover?tab=museum"
            className="letter-card rounded-xl p-5 flex items-center gap-3 hover:shadow-md transition-shadow group"
          >
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[hsl(var(--opp-accent-soft))]">
              <BookOpen className="h-5 w-5 text-[hsl(var(--opp-accent-strong))]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-[hsl(var(--opp-ink))]">{t('museum.title')}</p>
              <p className="text-xs text-[hsl(var(--opp-muted))] truncate">{t('museum.subtitle')}</p>
            </div>
            <ArrowRight className="h-4 w-4 text-[hsl(var(--opp-muted))] group-hover:text-[hsl(var(--opp-accent-strong))] transition-colors" />
          </Link>
        </aside>
      </div>

      {/* ===== Mobile-only sections (below feed) ===== */}
      <div className="lg:hidden space-y-4">
        {/* Mobile write CTA */}
        <Link
          to="/write"
          className="letter-card flex items-center gap-3 rounded-xl p-4"
        >
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[hsl(var(--opp-accent-soft))]">
            <PenLine className="h-5 w-5 text-[hsl(var(--opp-accent-strong))]" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-[hsl(var(--opp-ink))]">{t('feed.writeCard.title')}</p>
            <p className="text-xs text-[hsl(var(--opp-muted))]">{t('feed.writeCard.desc')}</p>
          </div>
          <ChevronRight className="h-4 w-4 text-[hsl(var(--opp-muted))]" />
        </Link>

        {/* Mobile hot letters */}
        {museumItems && museumItems.length > 0 && (
          <div className="letter-card rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="flex items-center gap-1.5 text-sm font-semibold text-[hsl(var(--opp-ink))]">
                <Flame className="h-4 w-4 text-[hsl(var(--opp-accent-strong))]" />
                {t('feed.hotLetters')}
              </h3>
              <Link to="/discover" className="inline-flex min-h-11 items-center px-1 text-xs text-[hsl(var(--opp-muted))] hover:text-[hsl(var(--opp-accent-strong))]">
                {t('common.viewAll')} <ChevronRight className="inline h-3 w-3" />
              </Link>
            </div>
            <div className="flex gap-3 overflow-x-auto pb-1">
              {museumItems.slice(0, 5).map((item) => (
                <div key={item.id} className="shrink-0 w-36 rounded-lg bg-[hsl(var(--opp-surface-raised))] p-3">
                  <p className="text-xs font-medium text-[hsl(var(--opp-ink))] line-clamp-2">{item.title}</p>
                  <span className="flex items-center gap-0.5 mt-1.5 text-caption text-[hsl(var(--opp-muted))]">
                    <Heart className="h-3 w-3" /> {item.like_count ?? 0}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Mobile tags */}
        <div className="flex flex-wrap gap-2">
          {TAGS.map((tag) => (
            <span
              key={tag.label}
              className={`rounded-full px-3 py-1 text-xs font-medium ${tag.color}`}
            >
              {tag.label}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Feed Card Component ──
function LetterFeedCard({
  letter,
  isLiked,
  onToggleLike,
  t,
}: {
  letter: { id: string; title?: string; content: string; created_at: string; style?: string; like_count?: number; is_anonymous?: boolean; author_name?: string };
  isLiked: boolean;
  onToggleLike: () => void;
  t: (key: string) => string;
}) {
  const paperClass = letter.style ? `letter-paper-${letter.style}` : '';

  return (
    <div className={`letter-card overflow-hidden rounded-xl transition-all hover:shadow-md ${paperClass}`}>
      <div className="p-5">
        {/* Header row */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--opp-accent-soft))] text-xs font-bold text-[hsl(var(--opp-ink))]">
              {letter.is_anonymous ? 'A' : (letter.author_name?.charAt(0).toUpperCase() ?? '?')}
            </div>
            <div className="min-w-0">
              {letter.is_anonymous ? (
                <p className="text-xs font-medium text-[hsl(var(--opp-muted))]">{t('write.anonymous.sender')}</p>
              ) : (
                <p className="text-xs font-medium text-[hsl(var(--opp-accent-strong))]">{letter.author_name ?? t('feed.anonymous')}</p>
              )}
              <p className="text-caption text-[hsl(var(--opp-muted))]">
                <Clock className="inline h-3 w-3 mr-0.5" />
                {new Date(letter.created_at).toLocaleDateString()}
              </p>
            </div>
          </div>
          {letter.style && (
            <span className="shrink-0 rounded-full bg-[hsl(var(--opp-accent-soft))] px-2 py-0.5 text-caption font-medium text-[hsl(var(--opp-accent-strong))]">
              {letter.style}
            </span>
          )}
        </div>

        {/* Title */}
        {letter.title && (
          <h3 className="mt-3 font-serif text-base font-bold text-[hsl(var(--opp-ink))] line-clamp-1">
            {letter.title}
          </h3>
        )}

        {/* Content preview */}
        <p className="mt-2 text-sm leading-relaxed text-[hsl(var(--opp-ink))] line-clamp-4">
          {letter.content}
        </p>

        {/* Actions */}
        <div className="mt-4 flex items-center justify-between">
          <OppButton
            onClick={onToggleLike}
            aria-label={isLiked ? 'Unlike' : 'Like'}
            aria-pressed={isLiked}
            className={`flex min-h-11 min-w-11 items-center justify-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition-all ${
              isLiked
                ? 'bg-[hsl(var(--opp-danger-bg))] text-[hsl(var(--opp-danger-fg))]'
                : 'bg-[hsl(var(--opp-surface-raised))] text-[hsl(var(--opp-accent-strong))] hover:bg-[hsl(var(--opp-accent-soft))]'
            }`}
          >
            <Heart className="h-3.5 w-3.5" fill={isLiked ? 'currentColor' : 'none'} />
            {letter.like_count ?? 0}
          </OppButton>
          <Link
            to="/discover"
            className="flex min-h-11 min-w-11 items-center justify-center text-xs text-[hsl(var(--opp-muted))] hover:text-[hsl(var(--opp-accent-strong))] transition-colors"
          >
            {t('feed.readMore')} <ChevronRight className="inline h-3 w-3" />
          </Link>
        </div>
      </div>
    </div>
  );
}
